import { ReactElement, useState } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import Add from '@mui/icons-material/Add'
import { Button, Stack, Typography } from '@mui/material'
import type { ProjectFieldDTO } from '@api/fastAPI.schemas'
import {
  useDeleteOrganisationProjectField,
  useGetOrganisationProjectFields,
} from '@api/organisation-project-fields/organisation-project-fields'
import { LoadingIndicator } from '@ui/feedback'
import { useSnackbar } from '@ui/snackbar'
import ManagerSection from '../ManagerSection'
import ProjectFieldDialog from './components/ProjectFieldDialog'
import ProjectFieldTable from './components/ProjectFieldTable'
import useInvalidateProjectFields from './hooks/useInvalidateProjectFields'

interface Props {
  readOnly: boolean
}

type DialogState = { field?: ProjectFieldDTO } | null

/**
 * Projektdatenfelder (Projektkonfiguration tab of the global Systemmanager): the organisation's own
 * project data fields. Every project of the organisation must fill them in on the project form, and
 * the documentation templates print them as `{{project_data.<Titel>}}`.
 */
const OrganisationProjectFields = ({ readOnly }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const invalidate = useInvalidateProjectFields()
  const [dialog, setDialog] = useState<DialogState>(null)

  const { data: fields, isLoading } = useGetOrganisationProjectFields<
    ProjectFieldDTO[],
    AxiosError
  >({
    query: {
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Projektdatenfelder'), {
          variant: 'error',
        })
      },
    },
  })

  const {
    mutate: deleteField,
    isLoading: isDeleting,
    variables: deleteVariables,
  } = useDeleteOrganisationProjectField<AxiosError, unknown>({
    mutation: {
      meta: {
        title: 'Projektdatenfeld löschen',
        description: 'Löscht ein Projektdatenfeld der Organisation',
      },
      onSuccess: async () => {
        await invalidate()
        enqueueSnackbar('Projektdatenfeld gelöscht', { variant: 'success' })
      },
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen des Projektdatenfelds'), {
          variant: 'error',
        })
      },
    },
  })

  if (isLoading) return <LoadingIndicator />

  return (
    <Stack spacing={3} data-cy="organisation-project-fields">
      <Typography variant="body2" color="text.secondary">
        Eigene Projektdaten, die bei jedem Projekt dieser Organisation im Projektformular ausgefüllt
        werden müssen – als Textfeld oder als Auswahlfeld mit vorgegebenen Optionen. Bestehende
        Projekte müssen die Felder beim nächsten Bearbeiten ergänzen. In den Vorlagen für Deckblatt
        und Kopf-/Fußzeile stehen die Felder als Platzhalter zur Verfügung, z. B.{' '}
        <code>{'{{project_data.Architekt}}'}</code>.
      </Typography>
      <ManagerSection
        title="Projektdatenfelder"
        actions={
          !readOnly && (
            <Button
              size="small"
              variant="contained"
              startIcon={<Add />}
              onClick={() => setDialog({})}
              data-cy="project-field-add"
            >
              Feld hinzufügen
            </Button>
          )
        }
      >
        <ProjectFieldTable
          fields={fields ?? []}
          onEdit={readOnly ? undefined : field => setDialog({ field })}
          onDelete={readOnly ? undefined : field => deleteField({ fieldGuid: field.guid })}
          deletingGuid={isDeleting ? deleteVariables?.fieldGuid : undefined}
        />
      </ManagerSection>
      {dialog && (
        <ProjectFieldDialog
          field={dialog.field}
          fields={fields ?? []}
          onClose={() => setDialog(null)}
        />
      )}
    </Stack>
  )
}

export default OrganisationProjectFields
